import React, { useCallback, useEffect, useMemo, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { useNavigate } from "react-router-dom";
import ProductLoader from "../Common/ProductLoader";
import { getSecondaryBanners } from "../../Utils/api-service";
import "./SecondaryBanner.css";

const AUTOPLAY_DELAY_MS = 3000;
const DEFAULT_DIVIDER_CLASS = "divider t-12 b-20";
/** Embla only loops when slides fill the viewport — duplicate until they do. */
const MIN_LOOP_SLIDES = 6;

export function normalizeBanners(rows) {
  if (!Array.isArray(rows)) return [];

  return rows
    .filter((row) => row?.imageUrl)
    .map((row, index) => ({
      id: row.id ?? `banner-${index}`,
      imageUrl: row.imageUrl,
      title: row.title,
      link: row.link,
    }));
}

function buildLoopSlides(banners) {
  const copies = Math.max(2, Math.ceil(MIN_LOOP_SLIDES / banners.length));
  const slides = [];

  for (let copy = 0; copy < copies; copy += 1) {
    banners.forEach((banner, index) => {
      slides.push({
        ...banner,
        slideKey: `${banner.id}__${copy}__${index}`,
      });
    });
  }

  return slides;
}

function SecondaryBannerSlide({ banner, onClick }) {
  const isInteractive = Boolean(banner.link || onClick);

  const handleClick = () => onClick?.(banner);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <div className="secondary-banner__slide">
      <div
        className={`secondary-banner__card${
          isInteractive ? " secondary-banner__card--interactive" : ""
        }`}
        onClick={isInteractive ? handleClick : undefined}
        onKeyDown={isInteractive ? handleKeyDown : undefined}
        role={isInteractive ? "button" : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        aria-label={banner.title || "Promotional banner"}
      >
        <img
          src={banner.imageUrl}
          alt={banner.title || ""}
          className="secondary-banner__image"
          loading="lazy"
          decoding="async"
          draggable={false}
        />
      </div>
    </div>
  );
}

function SecondaryBannerTrack({ banners, onBannerClick, autoplayEnabled }) {
  const loopSlides = useMemo(() => buildLoopSlides(banners), [banners]);

  const plugins = useMemo(
    () =>
      autoplayEnabled
        ? [
            Autoplay({
              delay: AUTOPLAY_DELAY_MS,
              stopOnInteraction: false,
            }),
          ]
        : [],
    [autoplayEnabled],
  );

  const [emblaRef] = useEmblaCarousel(
    {
      loop: true,
      align: "center",
      containScroll: false,
      duration: 25,
    },
    plugins,
  );

  return (
    <div className="secondary-banner__viewport" ref={emblaRef}>
      <div className="secondary-banner__container">
        {loopSlides.map((banner) => (
          <SecondaryBannerSlide
            key={banner.slideKey}
            banner={banner}
            onClick={onBannerClick}
          />
        ))}
      </div>
    </div>
  );
}

function wrapWithDividers(content, withDividers, dividerClassName) {
  if (!content) return null;
  if (!withDividers) return content;

  return (
    <>
      <div className={dividerClassName} />
      {content}
      <div className={dividerClassName} />
    </>
  );
}

function SecondaryBanner({
  banners: bannersProp,
  loading: loadingProp,
  autoFetch = true,
  fetchBanners = getSecondaryBanners,
  autoplay = true,
  showLoader = false,
  withDividers = false,
  dividerClassName = DEFAULT_DIVIDER_CLASS,
  className = "",
  onBannerClick,
}) {
  const navigate = useNavigate();
  const isControlled = bannersProp !== undefined;
  const [internalBanners, setInternalBanners] = useState([]);
  const [internalLoading, setInternalLoading] = useState(
    !isControlled && autoFetch,
  );
  const [reducedMotion] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );

  const banners = useMemo(
    () => (isControlled ? normalizeBanners(bannersProp) : internalBanners),
    [bannersProp, internalBanners, isControlled],
  );

  const loading = isControlled ? Boolean(loadingProp) : internalLoading;

  useEffect(() => {
    if (isControlled || !autoFetch) return undefined;

    let isMounted = true;

    fetchBanners()
      .then((response) => {
        if (!isMounted) return;
        setInternalBanners(
          response?.status === 200
            ? normalizeBanners(response?.data?.rows)
            : [],
        );
      })
      .catch((error) => {
        console.error("Error fetching banners:", error);
        if (isMounted) setInternalBanners([]);
      })
      .finally(() => {
        if (isMounted) setInternalLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [autoFetch, fetchBanners, isControlled]);

  const handleBannerClick = useCallback(
    (banner) => {
      if (onBannerClick) {
        onBannerClick(banner);
        return;
      }
      if (!banner.link) return;

      try {
        const url = new URL(banner.link, window.location.origin);
        navigate(url.pathname + url.search);
      } catch {
        window.location.href = banner.link;
      }
    },
    [navigate, onBannerClick],
  );

  const rootClassName = ["secondary-banner", className]
    .filter(Boolean)
    .join(" ");

  if (loading) {
    if (!showLoader) return null;

    return wrapWithDividers(
      <section
        className={rootClassName}
        aria-busy="true"
        aria-label="Loading banners"
      >
        <div className="secondary-banner__loader">
          <ProductLoader />
        </div>
      </section>,
      withDividers,
      dividerClassName,
    );
  }

  if (!banners.length) return null;

  let content;

  if (banners.length === 1) {
    content = (
      <section
        className={`${rootClassName} secondary-banner-pt-0 secondary-banner-pb-0`}
        aria-label="Promotional banners"
      >
        <div className="secondary-banner__track secondary-banner__track--single">
          <SecondaryBannerSlide
            banner={banners[0]}
            onClick={handleBannerClick}
          />
        </div>
      </section>
    );
  } else {
    content = (
      <section
        className={`${rootClassName} secondary-banner--loop secondary-banner-pt-0 secondary-banner-pb-0`}
        aria-label="Promotional banners"
      >
        <SecondaryBannerTrack
          banners={banners}
          onBannerClick={handleBannerClick}
          autoplayEnabled={autoplay && !reducedMotion}
        />
      </section>
    );
  }

  return wrapWithDividers(content, withDividers, dividerClassName);
}

export default SecondaryBanner;
